import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Activity, Server, Globe2, Star, Lock, Wifi } from 'lucide-react';
import toast from 'react-hot-toast';
import { streamFlowApi } from '@lib/streamFlowApi';
import type { ProxyNode, MeInfo } from '@lib/streamFlowApi';
import { supabase } from '@lib/supabase';
import { getApiBaseMode, getApiBaseUrl, probeTailnetReachable, setApiBaseMode } from '@lib/apiBase';

/**
 * Proxy nodes are the egress points the streaming proxy can run from.
 * Three are seeded out of the box (Azure VM, tk-bakery, NAS); admins
 * can edit hosts (e.g., to fill in the actual tailnet hostname for
 * tk-bakery once Tailscale is set up) and add new ones.
 */
export default function ProxyNodes() {
  const navigate = useNavigate();
  const [nodes, setNodes] = useState<ProxyNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [me, setMe] = useState<MeInfo | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    host: '',
    is_tailnet_only: true,
    is_default_community: false,
    available_for_checkout: true,
    is_shared: false,
  });

  // Tracks whether the current page's API calls (which go through the
  // currently-selected api base — tailnet or public) are succeeding.
  // When true while mode === 'tailnet', it proves the SPA is actually
  // talking to the tailnet host — i.e., tailnet is reachable for real
  // traffic regardless of what the standalone probe says.
  const [apiReachable, setApiReachable] = useState<boolean | null>(null);

  const fetchNodes = async () => {
    const res = await streamFlowApi.listProxyNodes();
    if (res.success) {
      setNodes(res.data || []);
      setApiReachable(true);
    } else {
      setApiReachable(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    Promise.all([streamFlowApi.getMe(), supabase.auth.getUser()]).then(([meRes, userRes]) => {
      if (meRes.success && meRes.data) setMe(meRes.data);
      setCurrentUserId(userRes.data.user?.id ?? null);
    });
    fetchNodes();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setFormData({ name: '', host: '', is_tailnet_only: true, is_default_community: false, available_for_checkout: true, is_shared: false });
  };

  const startEdit = (n: ProxyNode) => {
    setEditingId(n.id);
    setFormData({
      name: n.name,
      host: n.host,
      is_tailnet_only: n.is_tailnet_only,
      is_default_community: n.is_default_community,
      available_for_checkout: n.available_for_checkout,
      is_shared: n.is_shared,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Non-admins can't set is_shared — server enforces this; we mirror
    // client-side so the request is well-formed.
    const payload = me?.is_admin ? formData : { ...formData, is_shared: undefined };
    const res = editingId
      ? await streamFlowApi.updateProxyNode(editingId, payload)
      : await streamFlowApi.createProxyNode(payload);
    if (res.success) {
      toast.success(editingId ? 'Proxy node updated' : 'Proxy node added');
      setShowForm(false);
      resetForm();
      fetchNodes();
    } else {
      toast.error(res.error?.message || 'Save failed');
    }
  };

  const handleDelete = async (n: ProxyNode) => {
    if (!confirm(`Delete proxy node "${n.name}"? Any seats holding this node will be cleared.`)) return;
    const res = await streamFlowApi.deleteProxyNode(n.id);
    if (res.success) {
      toast.success('Deleted');
      fetchNodes();
    } else {
      toast.error(res.error?.message || 'Delete failed');
    }
  };

  const handleHealth = async (n: ProxyNode) => {
    toast.loading('Probing…', { id: 'pn-health' });
    const res = await streamFlowApi.checkProxyNodeHealth(n.id);
    if (res.success && res.data) {
      const ok = (res.data as { healthy?: boolean }).healthy === true;
      toast[ok ? 'success' : 'error'](
        ok ? `${n.name} is healthy` : `${n.name} is unreachable`,
        { id: 'pn-health' },
      );
      fetchNodes();
    } else {
      toast.error(res.error?.message || 'Probe failed', { id: 'pn-health' });
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      <ConnectionModeSection apiReachable={apiReachable} />


      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Proxy Nodes</h1>
          <p className="text-sm text-gray-500 mt-1">
            Egress points for streaming traffic. Pick one when checking out a provider; the dashboard uses your default for community streams.
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(!showForm); }}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm"
        >
          <Plus className="w-4 h-4" /> {showForm ? 'Cancel' : 'Add Node'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border rounded-lg p-6 mb-6 space-y-4">
          <h3 className="font-semibold">{editingId ? 'Edit proxy node' : 'New proxy node'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input type="text" required value={formData.name}
                onChange={e => setFormData(d => ({ ...d, name: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Home Windows" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Host (domain[:port] or full URL)</label>
              <input type="text" required value={formData.host}
                onChange={e => setFormData(d => ({ ...d, host: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                placeholder="tk-bakery.<tailnet>.ts.net:3006" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.is_tailnet_only}
                onChange={e => setFormData(d => ({ ...d, is_tailnet_only: e.target.checked }))} />
              Tailnet-only (viewers must be on Tailscale)
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.available_for_checkout}
                onChange={e => setFormData(d => ({ ...d, available_for_checkout: e.target.checked }))} />
              Available for checkout (24h exclusive seats)
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.is_default_community}
                onChange={e => setFormData(d => ({ ...d, is_default_community: e.target.checked }))} />
              Default for community streams (one per user)
            </label>
            {me?.is_admin && (
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={formData.is_shared}
                  onChange={e => setFormData(d => ({ ...d, is_shared: e.target.checked }))} />
                <span className="flex items-center gap-1.5">
                  <Globe2 className="w-4 h-4 text-blue-600" />
                  Share with all family users
                </span>
              </label>
            )}
          </div>
          <div className="flex gap-3">
            <button type="submit" className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm">
              {editingId ? 'Save changes' : 'Add node'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-gray-500 text-center py-8">Loading proxy nodes…</p>
      ) : nodes.length === 0 ? (
        <div className="text-center py-16 bg-white border rounded-lg">
          <Server className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No proxy nodes registered.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {nodes.map(n => {
            const isOwner = currentUserId !== null && n.user_id === currentUserId;
            const canEdit = isOwner || (me?.is_admin && n.is_shared);
            return (
              <div key={n.id} className="bg-white border rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${
                    n.health_status === 'healthy' ? 'bg-green-500' :
                    n.health_status === 'unhealthy' ? 'bg-red-500' : 'bg-gray-300'
                  }`} />
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{n.name}</p>
                      {n.is_default_community && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                          <Star className="w-3 h-3" />
                          Default
                        </span>
                      )}
                      {n.is_shared && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                          <Globe2 className="w-3 h-3" />
                          Community
                        </span>
                      )}
                      {n.is_tailnet_only && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-50 text-slate-700 border border-slate-200">
                          <Lock className="w-3 h-3" />
                          Tailnet-only
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 font-mono mt-0.5">{n.host}</p>
                    {n.last_health_check && (
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        last checked: {new Date(n.last_health_check).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canEdit && (
                    <button onClick={() => handleHealth(n)} className="p-2 hover:bg-gray-100 rounded-lg" title="Health check">
                      <Activity className="w-4 h-4" />
                    </button>
                  )}
                  {canEdit && (
                    <button onClick={() => startEdit(n)} className="px-2 py-1 text-xs hover:bg-gray-100 rounded-lg border">
                      Edit
                    </button>
                  )}
                  {isOwner && (
                    <button onClick={() => handleDelete(n)} className="p-2 hover:bg-red-50 rounded-lg text-red-500" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Connection-mode section: lets the user switch between hitting the API
 * via the public Azure-fronted hostname (default) and the tailnet
 * hostname. Tailnet mode auto-routes streams through the home proxy
 * nodes (no Azure egress) but only works when the device has Tailscale.
 *
 * Two reachability signals can be in play:
 *   - `apiReachable` (from parent) — whether the page's own API calls
 *     (running against the currently-selected base) succeeded. This is
 *     the ground truth: if true while in tailnet mode, the SPA IS
 *     talking to the tailnet host.
 *   - `probe` — a standalone, unauthenticated GET + WebRTC race. Useful
 *     when on public mode to detect "you could switch to tailnet".
 *
 * When already on tailnet, prefer the ground-truth signal. The probe
 * can show false negatives (CORS, WebRTC blocked) even when real
 * traffic flows fine, which confuses users.
 */
function ConnectionModeSection({ apiReachable }: { apiReachable: boolean | null }) {
  const [mode, setMode] = useState(getApiBaseMode());
  const [probe, setProbe] = useState<boolean | null>(null);
  // Dual-host reachability check. Hits /api/debug/whoami on both the
  // public and tailnet hosts so the user can see exactly which paths
  // their browser can resolve. Surfaces the truth when the synthetic
  // probe and the api-base mode disagree.
  const [dualProbe, setDualProbe] = useState<{ public: 'ok' | 'fail' | 'loading' | null; tailnet: 'ok' | 'fail' | 'loading' | null }>({ public: null, tailnet: null });

  useEffect(() => {
    let cancelled = false;
    probeTailnetReachable(2500).then((ok) => {
      if (!cancelled) setProbe(ok);
    });
    return () => { cancelled = true; };
  }, []);

  const runDualProbe = async () => {
    setDualProbe({ public: 'loading', tailnet: 'loading' });
    const probeOne = async (url: string): Promise<'ok' | 'fail'> => {
      try {
        const ac = new AbortController();
        const t = setTimeout(() => ac.abort(), 6000);
        const r = await fetch(url, { signal: ac.signal, mode: 'cors', cache: 'no-store' });
        clearTimeout(t);
        return r.ok ? 'ok' : 'fail';
      } catch {
        return 'fail';
      }
    };
    const [pub, tail] = await Promise.all([
      probeOne('https://gastv-api.gasweb.info/api/debug/whoami'),
      probeOne('https://gastv-backend.taile0df79.ts.net/api/debug/whoami'),
    ]);
    setDualProbe({ public: pub, tailnet: tail });
  };

  const tailnetActive = mode === 'tailnet';
  const currentBase = getApiBaseUrl();

  // Critical: detect when a build-time env override (VITE_STREAMFLOW_API_URL,
  // typically set on Vercel) is forcing a specific API base regardless of
  // the runtime toggle. Without this check the status line happily claims
  // "Tailnet active" while currentBase points at gastv-api — confusing.
  // Compare what the toggle SAYS we should be using vs what we ACTUALLY use.
  const expectedBase = tailnetActive
    ? 'https://gastv-backend.taile0df79.ts.net/api'
    : 'https://gastv-api.gasweb.info/api';
  const overrideForced = currentBase !== expectedBase;

  // Status messaging. The ground-truth signal (apiReachable) wins
  // whenever it's available — it's a real authenticated round-trip
  // against the current base, not a synthetic probe that can fail for
  // unrelated reasons.
  let statusMsg: string;
  let statusTone: 'good' | 'warn' | 'neutral';
  if (overrideForced) {
    statusMsg = `⚠ Toggle is decorative on this build — VITE_STREAMFLOW_API_URL is forcing ${currentBase}`;
    statusTone = 'warn';
  } else if (tailnetActive && apiReachable === true) {
    statusMsg = '✓ Tailnet active — API calls are reaching the tailnet host';
    statusTone = 'good';
  } else if (tailnetActive && apiReachable === false) {
    statusMsg = '⚠ Tailnet selected but API calls are failing — switch back to Public';
    statusTone = 'warn';
  } else if (tailnetActive) {
    statusMsg = 'Tailnet active — verifying reachability…';
    statusTone = 'neutral';
  } else if (probe === null) {
    statusMsg = 'probing tailnet…';
    statusTone = 'neutral';
  } else if (probe) {
    statusMsg = '✓ tailnet reachable from this device';
    statusTone = 'good';
  } else {
    statusMsg = '⚠ probe didn\'t see tailnet (browser DNS may bypass it — click Tailnet to force-try)';
    statusTone = 'warn';
  }
  const toneClass =
    statusTone === 'good' ? 'text-emerald-700' :
    statusTone === 'warn' ? 'text-amber-700' :
    'text-gray-500';

  return (
    <div className="bg-white border rounded-lg p-4 mb-6">
      <div className="flex items-center gap-2 mb-2">
        <Wifi className="w-4 h-4 text-blue-600" />
        <h2 className="text-sm font-semibold">Connection mode</h2>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Tailnet mode routes API traffic over the gasweb.info Tailscale network and lets
        the resolver pick a home proxy node — no Azure egress. Public mode is the default
        and works from anywhere; pick tailnet only if this device has Tailscale installed.
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => { if (tailnetActive) setApiBaseMode('public'); }}
            className={`px-3 py-1.5 rounded-md text-xs font-medium ${
              !tailnetActive ? 'bg-white shadow text-slate-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Public (Azure)
          </button>
          <button
            type="button"
            onClick={() => {
              if (tailnetActive) return;
              if (probe === false) {
                const proceed = confirm(
                  'Tailnet probe failed from this browser — Tailscale may be off, your browser may be bypassing tailnet DNS (Chrome "Use secure DNS" / iOS Private Relay are common culprits), or the first cert handshake is still pending.\n\nSwitch anyway? If the SPA can\'t reach the tailnet host you\'ll see API errors and can flip back to Public from this page.',
                );
                if (!proceed) return;
              }
              setApiBaseMode('tailnet');
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-medium ${
              tailnetActive ? 'bg-white shadow text-slate-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Tailnet
          </button>
        </div>
        <span className={`text-[11px] ${toneClass}`}>{statusMsg}</span>
      </div>
      {tailnetActive && apiReachable === true && (
        <p className="text-[11px] text-blue-700 mt-2">
          Streams will pick home proxy nodes automatically when no checkout pin is set.
          WebSocket events also route via tailnet.
        </p>
      )}

      <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[11px] text-gray-500">
            Currently calling: <span className="font-mono text-gray-700 break-all">{currentBase}</span>
          </p>
          <button
            type="button"
            onClick={runDualProbe}
            className="text-[11px] px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 flex-shrink-0"
          >
            Probe both hosts
          </button>
        </div>
        {(dualProbe.public || dualProbe.tailnet) && (
          <div className="text-[11px] space-y-1 bg-gray-50 rounded p-2 font-mono">
            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-600">gastv-api.gasweb.info (public):</span>
              <span className={
                dualProbe.public === 'ok' ? 'text-emerald-700' :
                dualProbe.public === 'fail' ? 'text-red-700' :
                'text-gray-400'
              }>
                {dualProbe.public === 'loading' ? '…' : dualProbe.public === 'ok' ? '✓ reachable' : '✗ unreachable'}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-600">gastv-backend.taile0df79.ts.net (tailnet):</span>
              <span className={
                dualProbe.tailnet === 'ok' ? 'text-emerald-700' :
                dualProbe.tailnet === 'fail' ? 'text-red-700' :
                'text-gray-400'
              }>
                {dualProbe.tailnet === 'loading' ? '…' : dualProbe.tailnet === 'ok' ? '✓ reachable' : '✗ unreachable'}
              </span>
            </div>
            {dualProbe.public === 'ok' && dualProbe.tailnet === 'fail' && (
              <p className="text-[10px] text-amber-700 mt-1 font-sans">
                Your browser can reach Azure but not the tailnet host. Common cause: Chrome "Use secure DNS" or iOS Private Relay bypassing Tailscale MagicDNS. Either disable secure DNS for this site, or install the Capacitor mobile app (which uses the OS resolver).
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
